import useMediaQuery from '@mui/material/useMediaQuery';
import React, {
  useRef,
  useEffect,
  useContext,
  useCallback
} from 'react';
import {
  Canvas,
  Dpr,
  invalidate,
  useThree
} from '@react-three/fiber';
import { ArcballControls, OrbitControls } from '@react-three/drei';
import { useTheme } from '@mui/material/styles';
import * as THREE from 'three';
import type { ArcballControls as ArcballControlsImpl, OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import useMenuState from 'components/Layout/MenuStateProvider';
import { DRAWER_WIDTH, DRAWER_WIDTH_CLOSED, DRAWER_WIDTH_CLOSED_SM } from 'components/Layout/utils';
import ModelRenderer from './ModelRenderer';
import { IntersectionData } from './MineModel/intersectionTypes';
import { Model } from './hooks/useModelState';
import { PCNode } from './MineModel/Nodes';
import SceneManagerContext from './hooks/SceneManagerProvider/sceneManagerContext';
import PCSpool from './MineModel/Spools/PCSpool';
import { cadToThreeJsPosition } from './Scenes/scene-utils';
import useFlatRotate from './hooks/useFlatRotate';
import { useMarquee } from './contexts/MarqueeContext';
import { focusCameraOnPoint } from './MineModel/cameraFraming';
import { getMeasureState, subscribeToMeasureState } from './Scenes/BulkEditScene/Toolbar/measureState';
import ViewCube, { ViewCubeSync } from './Components/ViewCube/ViewCube';
import { POLE_MARGIN } from './Components/ViewCube/viewCubeCamera';

export type SceneControls = ArcballControlsImpl | OrbitControlsImpl;

const VIEW_CUBE_BOTTOM = 8;

interface CanvasProps {
  model: Model | null;
  onNodeHover: (node: PCNode | null) => void;
  onNodeSelect: (node: PCNode, stopeId?: string) => void;
  onPlantIconSelect?: (node: PCNode) => void;
  onSpoolSelect: (spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => void;
  onStopeShapeSelect?: (stopeId: string) => void;
  onIntersections?: (intersections: IntersectionData[]) => void;
  onCameraReady?: (camera: THREE.OrthographicCamera, controls: SceneControls) => void;
  onModelTransform?: (offset: THREE.Vector3, scale: number) => void;
  zUpMode?: boolean;
  onZUpModeChange?: (zUpMode: boolean) => void;
  dpr?: Dpr;
}

export interface FocusOptions {
  radius?: number;
  padding?: number;
}

export type FocusHandler = (point: THREE.Vector3, options?: FocusOptions) => void;

const ControlsWrapper = ({
  onFocusRef,
  controlsRef,
  zUpMode,
  onControlsChanged,
}: {
  onFocusRef: React.MutableRefObject<FocusHandler | null>;
  controlsRef: React.MutableRefObject<SceneControls | null>;
  zUpMode?: boolean;
  onControlsChanged?: () => void;
}) => {
  const arcballRef = useRef<ArcballControlsImpl | null>(null);
  const orbitRef = useRef<OrbitControlsImpl | null>(null);
  const activeRef = zUpMode ? orbitRef : arcballRef;
  const lastTargetRef = useRef(new THREE.Vector3());
  const camera = useThree((state) => state.camera);

  const handleFocus = useCallback<FocusHandler>((point, options) => {
    const controls = activeRef.current as any;
    if (!controls) return;

    const radius = options?.radius;
    const hasRadius = typeof radius === 'number' && radius > 0;

    if (hasRadius) {
      focusCameraOnPoint(controls, camera, point, options);
    } else if (controls.focus) {
      controls.focus(point, controls.scaleFactor || 0.8);
      controls.update();
    } else if (controls.target) {
      controls.target.set(point.x, point.y, point.z);
      controls.update();
    }

    invalidate();
  }, [zUpMode, camera]);

  useEffect(() => {
    const ref = onFocusRef;
    ref.current = handleFocus;
    return () => {
      ref.current = null;
    };
  }, [handleFocus, onFocusRef]);

  useEffect(() => {
    const ref = controlsRef;
    const prevControls = ref.current;
    if (prevControls) {
      lastTargetRef.current.copy(prevControls.target);
    }

    camera.up.set(0, 1, 0);
    camera.updateMatrixWorld();

    const newControls = activeRef.current;
    if (newControls) {
      newControls.target.copy(lastTargetRef.current);
      newControls.update();
    }

    ref.current = newControls;
    onControlsChanged?.();
    invalidate();

    return () => {
      if (ref.current) {
        lastTargetRef.current.copy(ref.current.target);
      }
      ref.current = null;
    };
  }, [controlsRef, zUpMode, camera, onControlsChanged]);

  if (zUpMode) {
    return (
      <OrbitControls
        ref={orbitRef}
        makeDefault
        enableZoom
        enablePan
        enableDamping={false}
        zoomToCursor
        rotateSpeed={0.5}
        minPolarAngle={POLE_MARGIN}
        maxPolarAngle={Math.PI - POLE_MARGIN}
      />
    );
  }

  return <ArcballControls ref={arcballRef} makeDefault enableZoom enablePan cursorZoom dampingFactor={100} />;
};

// Rendering above the device pixel ratio makes the browser downsample the canvas, which splits
// WebGL's 1px lines across pixel rows and leaves bright/dim streaks along every spool.
const DEVICE_DPR: Dpr = [1, 2];

const SceneBackground = () => {
  const scene = useThree((s) => s.scene);
  const [backgroundColour, setBackgroundColour] = React.useState(() => getMeasureState().backgroundColour);

  useEffect(() => subscribeToMeasureState(() => {
    setBackgroundColour(getMeasureState().backgroundColour);
  }), []);

  useEffect(() => {
    scene.background = new THREE.Color(backgroundColour);
    invalidate();
  }, [scene, backgroundColour]);

  return null;
};

const ThreeCanvas = ({
  model, onNodeHover, onNodeSelect, onPlantIconSelect, onSpoolSelect,
  onStopeShapeSelect, onIntersections, onCameraReady, onModelTransform,
  zUpMode, onZUpModeChange, dpr = DEVICE_DPR,
}: CanvasProps) => {
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const controlsRef = useRef<SceneControls | null>(null);
  const focusHandlerRef = useRef<FocusHandler | null>(null);
  const viewCubeInvalidateRef = useRef<(() => void) | null>(null);
  const hasInitializedRef = useRef(false);
  const zUpModeRef = useRef(zUpMode);

  useEffect(() => {
    zUpModeRef.current = zUpMode;
  }, [zUpMode]);
  const menuOpen = useMenuState();
  const muiTheme = useTheme();
  const isSmallUp = useMediaQuery(muiTheme.breakpoints.up('sm'));
  let drawerSpacing;

  if (menuOpen) {
    drawerSpacing = DRAWER_WIDTH + 100;
  } else if (isSmallUp) {
    drawerSpacing = DRAWER_WIDTH_CLOSED + 100;
  } else {
    drawerSpacing = DRAWER_WIDTH_CLOSED_SM + 100;
  }

  const STORAGE_KEY_PREFIX = 'backfill-planner-camera-view-';

  const getStorageKey = useCallback(() => {
    const projectId = model?.project?.entityId;
    return projectId ? `${STORAGE_KEY_PREFIX}${projectId}` : null;
  }, [model?.project?.entityId]);

  interface SavedCameraState {
    zUpMode?: boolean;
    target: { x: number; y: number; z: number };
    zoom: number;
    cameraPosition?: { x: number; y: number; z: number };
    frustum?: { left: number; right: number; top: number; bottom: number };
    cameraMatrixState0: number[];
    gizmoMatrixState0: number[];
    up0: { x: number; y: number; z: number };
    zoom0: number;
    fov0: number;
  }

  const pendingSavedStateRef = useRef<SavedCameraState | null>(null);
  // In-memory snapshot of the default-scene camera taken when entering the
  // stope fill view, restored on the way back. The stope fill view mutates the
  // shared ortho camera (frustum + position) and overwrites the ArcballControls
  // reset baseline, so without this the camera stays framed on the tiny stope.
  const preFillCameraStateRef = useRef<SavedCameraState | null>(null);

  const applySavedState = useCallback((state: SavedCameraState) => {
    if (!cameraRef.current || !controlsRef.current) return false;

    const controls = controlsRef.current as any;

    /* eslint-disable no-underscore-dangle */
    if (controls._cameraMatrixState0) {
      if (state.cameraMatrixState0?.length === 16) {
        controls._cameraMatrixState0.fromArray(state.cameraMatrixState0);
      }
      if (state.gizmoMatrixState0?.length === 16 && controls._gizmoMatrixState0) {
        controls._gizmoMatrixState0.fromArray(state.gizmoMatrixState0);
      }
      if (controls._up0) {
        controls._up0.set(state.up0.x, state.up0.y, state.up0.z);
      }
      if (controls._zoom0 !== undefined) {
        controls._zoom0 = state.zoom0;
      }
      if (controls._fov0 !== undefined) {
        controls._fov0 = state.fov0;
      }
      controls.reset();
    }
    /* eslint-enable no-underscore-dangle */

    if (state.cameraPosition) {
      cameraRef.current.position.set(state.cameraPosition.x, state.cameraPosition.y, state.cameraPosition.z);
    }

    if (state.frustum) {
      cameraRef.current.left = state.frustum.left;
      cameraRef.current.right = state.frustum.right;
      cameraRef.current.top = state.frustum.top;
      cameraRef.current.bottom = state.frustum.bottom;
    }

    if (controls.setTarget) {
      controls.setTarget(state.target.x, state.target.y, state.target.z);
    } else {
      controls.target.set(state.target.x, state.target.y, state.target.z);
    }

    cameraRef.current.zoom = state.zoom;
    cameraRef.current.updateProjectionMatrix();
    controls.update();

    invalidate();
    return true;
  }, []);

  const captureCameraState = useCallback((): SavedCameraState | null => {
    if (!cameraRef.current || !controlsRef.current) return null;

    const controls = controlsRef.current as any;
    controls.saveState();

    /* eslint-disable no-underscore-dangle */
    const targetSource = controls._gizmos?.position ?? controls.target;
    const state: SavedCameraState = {
      zUpMode: zUpModeRef.current ?? false,
      target: {
        x: targetSource.x,
        y: targetSource.y,
        z: targetSource.z,
      },
      zoom: cameraRef.current.zoom || 1,
      cameraPosition: {
        x: cameraRef.current.position.x,
        y: cameraRef.current.position.y,
        z: cameraRef.current.position.z,
      },
      frustum: {
        left: cameraRef.current.left,
        right: cameraRef.current.right,
        top: cameraRef.current.top,
        bottom: cameraRef.current.bottom,
      },
      cameraMatrixState0: controls._cameraMatrixState0?.toArray() || [],
      gizmoMatrixState0: controls._gizmoMatrixState0?.toArray() || [],
      up0: {
        x: controls._up0?.x ?? 0,
        y: controls._up0?.y ?? 1,
        z: controls._up0?.z ?? 0,
      },
      zoom0: controls._zoom0 ?? 1,
      fov0: controls._fov0 ?? 50,
    };
    /* eslint-enable no-underscore-dangle */

    return state;
  }, []);

  const handleSaveView = useCallback(() => {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    const state = captureCameraState();
    if (!state) return;

    localStorage.setItem(storageKey, JSON.stringify(state));
  }, [getStorageKey, captureCameraState]);

  const handleLoadView = useCallback(() => {
    if (!cameraRef.current || !controlsRef.current) return;

    const storageKey = getStorageKey();
    if (!storageKey) return;

    const stored = localStorage.getItem(storageKey);
    if (!stored) return;

    const state: SavedCameraState = JSON.parse(stored);
    const savedZUpMode = state.zUpMode ?? false;

    if (savedZUpMode !== (zUpModeRef.current ?? false)) {
      pendingSavedStateRef.current = state;
      onZUpModeChange?.(savedZUpMode);
    } else {
      applySavedState(state);
    }
  }, [getStorageKey, applySavedState, onZUpModeChange]);

  const context = useContext(SceneManagerContext);
  const shouldSaveView = context?.state.shouldSaveView;
  const shouldLoadView = context?.state.shouldLoadView;

  useEffect(() => {
    if (shouldSaveView) {
      handleSaveView();
    }
  }, [shouldSaveView, handleSaveView]);

  useEffect(() => {
    if (shouldLoadView) {
      handleLoadView();
    }
  }, [shouldLoadView, handleLoadView]);

  // Snapshot the camera when the stope fill view opens and restore it when it
  // closes, so navigating back to the default scene returns to the prior view
  // instead of leaving the camera framed on the (now hidden) stope.
  const stopeFillActive = !!model?.stopeFillState;
  const prevStopeFillActiveRef = useRef(stopeFillActive);
  useEffect(() => {
    const wasActive = prevStopeFillActiveRef.current;
    prevStopeFillActiveRef.current = stopeFillActive;

    if (!wasActive && stopeFillActive) {
      preFillCameraStateRef.current = captureCameraState();
    } else if (wasActive && !stopeFillActive && preFillCameraStateRef.current) {
      const state = preFillCameraStateRef.current;
      preFillCameraStateRef.current = null;
      // Defer one frame so the default-scene model has remounted before we
      // re-apply the camera; otherwise its mount effects can clobber it.
      requestAnimationFrame(() => applySavedState(state));
    }
  }, [stopeFillActive, captureCameraState, applySavedState]);

  const handleControlsChanged = useCallback(() => {
    if (!pendingSavedStateRef.current || !controlsRef.current) return;
    const state = pendingSavedStateRef.current;
    pendingSavedStateRef.current = null;
    applySavedState(state);
    if (onCameraReady && cameraRef.current && controlsRef.current) {
      onCameraReady(cameraRef.current, controlsRef.current);
    }
  }, [applySavedState, onCameraReady]);

  const handleNodeFocus = useCallback((node: PCNode, worldPosition?: THREE.Vector3) => {
    if (focusHandlerRef.current) {
      if (worldPosition) {
        focusHandlerRef.current(worldPosition);
      } else {
        const nodePosition = cadToThreeJsPosition(node.coords);
        focusHandlerRef.current(nodePosition);
      }
    }
  }, []);

  const handleSpoolFocus = useCallback((spool: PCSpool, worldPosition?: THREE.Vector3) => {
    if (focusHandlerRef.current) {
      if (worldPosition) {
        focusHandlerRef.current(worldPosition);
      } else if (spool.pointA) {
        const spoolPosition = cadToThreeJsPosition(spool.pointA.coords);
        focusHandlerRef.current(spoolPosition);
      }
    }
  }, []);

  const handleRouteFocus = useCallback((route: any, worldPosition?: THREE.Vector3) => {
    if (focusHandlerRef.current) {
      if (worldPosition) {
        focusHandlerRef.current(worldPosition);
      } else if (route?.routePoints && route.routePoints.length > 0) {
        const firstPoint = route.routePoints[0];
        if (firstPoint && firstPoint.point) {
          const routePosition = cadToThreeJsPosition(firstPoint.point.coords);
          focusHandlerRef.current(routePosition);
        }
      }
    }
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMarqueeMode } = useMarquee();
  const { isRotating } = useFlatRotate({
    containerRef, cameraRef, controlsRef, enabled: !zUpMode, marqueeEnabled: isMarqueeMode,
  });

  useEffect(() => {
    if (!model || hasInitializedRef.current) return;

    const nodes = Object.values(model.nodes || {});
    if (nodes.length === 0) return;

    const points = nodes
      .filter((n) => n && n.coords && typeof n.coords.x === 'number' && typeof n.coords.y === 'number' && typeof n.coords.z === 'number')
      .map((n) => cadToThreeJsPosition(n.coords));

    if (points.length === 0) return;

    const box = new THREE.Box3().setFromPoints(points);
    const sphere = new THREE.Sphere();
    box.getBoundingSphere(sphere);
    const R = Math.max(sphere.radius, 1);
    const distance = R * 3;
    const center = new THREE.Vector3(0, 0, 0);
    const cameraPosition = new THREE.Vector3(0, 0, distance);

    const initializeCamera = () => {
      if (!cameraRef.current || !controlsRef.current) {
        requestAnimationFrame(initializeCamera);
        return;
      }

      const near = -1000000;
      const far = 1000000;

      const viewSize = R * 1.5;
      const canvas = document.querySelector('canvas');
      const aspect = canvas ? canvas.clientWidth / canvas.clientHeight : 1;
      cameraRef.current.left = -viewSize * aspect;
      cameraRef.current.right = viewSize * aspect;
      cameraRef.current.top = viewSize;
      cameraRef.current.bottom = -viewSize;
      cameraRef.current.near = near;
      cameraRef.current.far = far;
      cameraRef.current.updateProjectionMatrix();

      cameraRef.current.position.copy(cameraPosition);
      cameraRef.current.lookAt(center);

      if (controlsRef.current) {
        const controls = controlsRef.current as any;
        if (controls.setTarget) {
          controls.setTarget(center.x, center.y, center.z);
        } else {
          controls.target.set(center.x, center.y, center.z);
        }
        controls.camera = cameraRef.current;
        controlsRef.current.update();
      }

      let needsModeSwitch = false;
      let appliedSavedState = false;
      const storageKey = getStorageKey();
      if (storageKey) {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const savedState: SavedCameraState = JSON.parse(stored);
          const savedZUpMode = savedState.zUpMode ?? false;
          if (savedZUpMode !== (zUpModeRef.current ?? false)) {
            pendingSavedStateRef.current = savedState;
            onZUpModeChange?.(savedZUpMode);
            needsModeSwitch = true;
          } else {
            applySavedState(savedState);
            appliedSavedState = true;
          }
        }
      }

      if (!needsModeSwitch && !appliedSavedState && controlsRef.current) {
        controlsRef.current.saveState();
        controlsRef.current.reset();
      }

      hasInitializedRef.current = true;
      invalidate();

      if (!needsModeSwitch && onCameraReady && cameraRef.current && controlsRef.current) {
        onCameraReady(cameraRef.current, controlsRef.current);
      }
    };

    requestAnimationFrame(initializeCamera);
  }, [model, getStorageKey, applySavedState, onCameraReady, onZUpModeChange]);

  return (
    <div id="stopes-view-canvas" ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Canvas
        orthographic
        camera={{ position: [0, 0, -20], zoom: 1, near: -1000000, far: 1000000 }}
        gl={{ alpha: false, antialias: true, logarithmicDepthBuffer: true, preserveDrawingBuffer: true }}
        dpr={dpr}
        onCreated={(state) => {
          cameraRef.current = state.camera as THREE.OrthographicCamera;
          Object.assign(state.raycaster.params, { Line: { threshold: 1 } });
          requestAnimationFrame(() => invalidate());
        }}
        frameloop="demand"
      >
        <SceneBackground />
        {!model?.stopeFillState && <ambientLight />}
        <ViewCubeSync invalidateRef={viewCubeInvalidateRef} />
        <ControlsWrapper onFocusRef={focusHandlerRef} controlsRef={controlsRef} zUpMode={zUpMode} onControlsChanged={handleControlsChanged} />
        {model && (
          <ModelRenderer
            model={model}
            onNodeHover={onNodeHover}
            onNodeSelect={onNodeSelect}
            onPlantIconSelect={onPlantIconSelect}
            onNodeFocus={handleNodeFocus}
            onSpoolFocus={handleSpoolFocus}
            onSpoolSelect={onSpoolSelect}
            onStopeShapeSelect={onStopeShapeSelect}
            onRouteFocus={handleRouteFocus}
            onIntersections={onIntersections}
            onModelTransform={onModelTransform}
          />
        )}
      </Canvas>
      <ViewCube
        cameraRef={cameraRef}
        controlsRef={controlsRef}
        invalidateRef={viewCubeInvalidateRef}
        zUpMode={!!zUpMode}
        centreX={drawerSpacing}
        bottom={VIEW_CUBE_BOTTOM}
      />
      {isRotating && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60vh',
            height: '60vh',
            borderRadius: '50%',
            border: '2px solid rgba(0, 255, 0, 0.4)',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

export default ThreeCanvas;
